import React, { useEffect, useLayoutEffect, useState } from "react";
import { useAuth } from "react-oidc-context";
import Spinner from "./Spinner";
import { isSignOutInProgress, RETURN_TO_KEY } from "../services/oidc";

const styles = {
  container: {
    minHeight: "100vh",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    textAlign: "center",
  },
  message: { marginTop: "16px", fontSize: "18px" },
  errorBox: {
    color: "#C62828",
    backgroundColor: "#FFEBEE",
    padding: "20px",
    borderRadius: "16px",
    fontSize: "18px",
    maxWidth: "500px",
  },
};

/**
 * Blocks the app until the visitor is authenticated.
 */
const AuthGate = ({ children, history }) => {
  const auth = useAuth();

  const shouldSignIn =
    !auth.isLoading &&
    !auth.isAuthenticated &&
    !auth.activeNavigator &&
    // Signing out also renders as "not authenticated" for the moment between
    // the user being dropped from the store and the browser leaving for
    // Keycloak. Starting a sign-in there would beat the sign-out to the
    // redirect and silently restore the session.
    !isSignOutInProgress() &&
    !auth.error;

  useEffect(() => {
    if (!shouldSignIn) return;
    // Remember where the visitor was headed so deep links (e.g. the voicemail
    // page reached from an SMS) survive the round-trip through Keycloak.
    // Kept in sessionStorage rather than the OIDC `state`, because it has to be
    // readable before the router mounts (see below), which is earlier than the
    // sign-in callback resolves.
    sessionStorage.setItem(
      RETURN_TO_KEY,
      window.location.pathname + window.location.search
    );
    auth.signinRedirect();
  }, [shouldSignIn, auth]);

  // Keycloak always returns to the origin ("/"), so the original path has to be
  // put back, and it must happen before the router mounts — index.js redirects
  // "/" to the dashboard on its first render.
  //
  // The navigation goes through the app's own history object, not
  // window.history. That object is created by setupRedux() when index.js is
  // first evaluated and captures window.location at that moment; since
  // replaceState fires no popstate, poking the address bar directly would leave
  // the router still convinced it is on "/". Rendering of children is held until
  // this has run so the router mounts on the right location.
  const [pathRestored, setPathRestored] = useState(false);

  useLayoutEffect(() => {
    if (pathRestored || !auth.isAuthenticated) return;

    const returnTo = sessionStorage.getItem(RETURN_TO_KEY);
    sessionStorage.removeItem(RETURN_TO_KEY);

    const current = window.location.pathname + window.location.search;
    if (returnTo && returnTo !== current) {
      if (history) {
        history.replace(returnTo);
      } else {
        window.history.replaceState({}, document.title, returnTo);
      }
    }

    setPathRestored(true);
  }, [pathRestored, auth.isAuthenticated, history]);

  if (auth.error) {
    return (
      <div style={styles.container}>
        <div style={styles.errorBox}>
          Sorry, we could not sign you in. Please try again, and reach out to
          Tech Support if the problem continues.
        </div>
      </div>
    );
  }

  if (!auth.isAuthenticated || !pathRestored) {
    return (
      <div style={styles.container}>
        <Spinner />
        <div style={styles.message}>Signing you in…</div>
      </div>
    );
  }

  return children;
};

export default AuthGate;
